import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  limit, 
  startAfter, 
  where,
  runTransaction,
  Timestamp,
  doc as firestoreDoc
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrash, 
  faReply, 
  faSpinner, 
  faCheck, 
  faTimes, 
  faPlus,
  faFilter,
  faChevronDown,
  faChevronUp,
  faThumbsUp,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './ProfileComponents.module.css';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import { createNotification } from '../../utils/NotificationService';

const FeedbackSection = ({ profileData: initialProfileData, isOwnProfile, showCustomAlert }) => {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();

  // Use local state for profileData to allow updates without requiring parent refresh
  const [profileData, setProfileData] = useState(initialProfileData);
  
  // State for feedback
  const [feedbackList, setFeedbackList] = useState([]);
  const [displayedFeedback, setDisplayedFeedback] = useState([]);
  const [lastFeedbackDoc, setLastFeedbackDoc] = useState(null);
  const [hasMoreFeedback, setHasMoreFeedback] = useState(true);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // State for tag-based caching
  const [tagFilteredFeedback, setTagFilteredFeedback] = useState({});
  const [tagLastFeedbackDocs, setTagLastFeedbackDocs] = useState({});
  const [tagHasMoreFeedback, setTagHasMoreFeedback] = useState({});
  
  // Form state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  
  // Filter state
  const [activeTagFilter, setActiveTagFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);

  const [isSingleItemView, setIsSingleItemView] = useState(false);
const [singleFeedback, setSingleFeedback] = useState(null);
const [singleItemId, setSingleItemId] = useState(null);
  
  // Feedback form state
  const [userFeedback, setUserFeedback] = useState({
    comment: '',
    tags: {}
  });
  
  // Define tag categories (these should be tailored to Crew/Staff)
  const tagCategories = {
    performance: {
      title: "Performance",
      tags: [
        "Professional",
        "Punctual",
        "Reliable",
        "Skilled",
        "Creative",
        "Team Player",
        "Leadership",
        "Problem Solver"
      ]
    },
    communication: {
      title: "Communication",
      tags: [
        "Clear Communication",
        "Poor Communication",
        "Responsive",
        "Unresponsive",
        "Friendly",
        "Difficult to Work With"
      ]
    },
    expertise: {
      title: "Expertise",
      tags: [
        "Technical Knowledge",
        "Experienced",
        "Novice",
        "Fast Learner",
        "Specialized Skills",
        "Versatile"
      ]
    },
    workEthic: {
      title: "Work Ethic",
      tags: [
        "Hard Worker",
        "Takes Initiative",
        "Lazy",
        "Detail-Oriented",
        "Adaptable",
        "Efficient"
      ]
    }
  };
  
  // Update local profileData when props change
  useEffect(() => {
    setProfileData(initialProfileData);
  }, [initialProfileData]);

  const fetchFeedbackCount = useCallback(async () => {
    if (!profileData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      const feedbackRef = collection(db, 'users', profileData.userId, 'feedback');
      const countQuery = query(feedbackRef);
      const snapshot = await getDocs(countQuery);
      
      // Update profileData with accurate count
      setProfileData(prev => ({
        ...prev,
        totalFeedback: snapshot.size
      }));
      
      // Also update the user document if the count is different
      if (snapshot.size !== (profileData.totalFeedback || 0)) {
        const userDocRef = firestoreDoc(db, 'users', profileData.userId);
        await updateDoc(userDocRef, {
          totalFeedback: snapshot.size
        });
      }
    } catch (err) {
      console.error('Error fetching feedback count:', err);
    }
  }, [profileData?.userId]);
  
  // Helper function to convert tags object to array
  const convertTagsToArray = (tagsObject) => {
    return Object.entries(tagsObject)
      .filter(([_, selected]) => selected === true)
      .map(([tag, _]) => tag);
  };
  
  // Helper function to convert tags array to object
  const convertTagsToObject = (tagsArray) => {
    if (!tagsArray || !Array.isArray(tagsArray)) return {};
    
    const tagsObject = {};
    tagsArray.forEach(tag => {
      tagsObject[tag] = true;
    });
    return tagsObject;
  };
  
  // Clear cache for all tag filters
  const clearTagCache = () => {
    setTagFilteredFeedback({});
    setTagLastFeedbackDocs({});
    setTagHasMoreFeedback({});
  };
  
  const navigateToUserProfile = (userName) => {
    if (userName) {
      navigate(`/${userName}`);
    }
  };

  // Load feedback with caching support
  const loadFeedback = useCallback(async (isInitial = false, tagFilter = 'all', forceRefresh = false) => {
    if (!profileData?.userId) return;
    
    setLoadingFeedback(true);
    
    try {
      // Check if we have cached data for this tag and it's not a forced refresh
      if (!forceRefresh && isInitial && tagFilteredFeedback[tagFilter] && tagFilteredFeedback[tagFilter].length > 0) {
        // Use cached data
        setDisplayedFeedback(tagFilteredFeedback[tagFilter]);
        setLastFeedbackDoc(tagLastFeedbackDocs[tagFilter]);
        setHasMoreFeedback(tagHasMoreFeedback[tagFilter] || false);
        
        // Set active tag filter if it changed
        if (tagFilter !== activeTagFilter) {
          setActiveTagFilter(tagFilter);
        }
        
        setLoadingFeedback(false);
        return;
      }
      
      const db = getFirestoreInstance();
      const feedbackRef = collection(db, 'users', profileData.userId, 'feedback');
      
      let feedbackQuery;
      
      // Apply tag filter if not 'all'
      if (tagFilter !== 'all') {
        // Use array-contains to filter by tag
        if (isInitial) {
          feedbackQuery = query(
            feedbackRef,
            where("tags", "array-contains", tagFilter),
            orderBy('timestamp', 'desc'),
            limit(5)
          );
        } else if (tagLastFeedbackDocs[tagFilter]) {
          feedbackQuery = query(
            feedbackRef,
            where("tags", "array-contains", tagFilter),
            orderBy('timestamp', 'desc'),
            startAfter(tagLastFeedbackDocs[tagFilter]),
            limit(5)
          );
        } else {
          setHasMoreFeedback(false);
          setLoadingFeedback(false);
          return;
        }
      } else {
        // No tag filter
        if (isInitial) {
          feedbackQuery = query(
            feedbackRef,
            orderBy('timestamp', 'desc'),
            limit(5)
          );
        } else if (tagLastFeedbackDocs[tagFilter]) {
          feedbackQuery = query(
            feedbackRef,
            orderBy('timestamp', 'desc'),
            startAfter(tagLastFeedbackDocs[tagFilter]),
            limit(5)
          );
        } else {
          setHasMoreFeedback(false);
          setLoadingFeedback(false);
          return;
        }
      }
      
      const feedbackSnapshot = await getDocs(feedbackQuery);
      
      // Update hasMoreFeedback state
      const hasMore = feedbackSnapshot.docs.length === 5;
      
      // Update tag-specific hasMore state
      setTagHasMoreFeedback(prev => ({
        ...prev,
        [tagFilter]: hasMore
      }));
      
      setHasMoreFeedback(hasMore);
      
      if (feedbackSnapshot.empty) {
        if (isInitial) {
          setDisplayedFeedback([]);
          
          // Update tag-specific cache with empty array
          setTagFilteredFeedback(prev => ({
            ...prev,
            [tagFilter]: []
          }));
        }
        
        setHasMoreFeedback(false);
        setTagHasMoreFeedback(prev => ({
          ...prev,
          [tagFilter]: false
        }));
      } else {
        // Get the last document
        const lastDoc = feedbackSnapshot.docs[feedbackSnapshot.docs.length - 1];
        
        // Update tag-specific lastDoc state
        setTagLastFeedbackDocs(prev => ({
          ...prev,
          [tagFilter]: lastDoc
        }));
        
        setLastFeedbackDoc(lastDoc);
        
        // Process feedback and fetch user data
        const feedbackData = await Promise.all(
          feedbackSnapshot.docs.map(async (feedbackDoc) => {
            const data = feedbackDoc.data();
            
            // Convert tags array to object for consistent UI handling
            const feedbackTags = convertTagsToObject(data.tags);
            
            // Try to get user data
            let userDataObj = null;
            
            try {
              // Fetch user data
              const userDocRef = firestoreDoc(db, 'users', data.userId);
              const userDoc = await getDoc(userDocRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                userDataObj = {
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
              
              // Fetch replies if any
              const repliesRef = collection(db, 'users', profileData.userId, 'feedback', feedbackDoc.id, 'replies');
              const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'), limit(2));
              const repliesSnapshot = await getDocs(repliesQuery);
              
              const replies = await Promise.all(
                repliesSnapshot.docs.map(async (replyDoc) => {
                  const replyData = replyDoc.data();
                  let replyUserData = null;
                  
                  // Fetch reply user data
                  try {
                    const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
                    const replyUserDoc = await getDoc(replyUserDocRef);
                    
                    if (replyUserDoc.exists()) {
                      const userData = replyUserDoc.data();
                      replyUserData = {
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        idVerificationStatus: userData.idVerificationStatus || ''
                      };
                    }
                  } catch (err) {
                    console.error('Error fetching reply user data:', err);
                  }
                  
                  return {
                    id: replyDoc.id,
                    ...replyData,
                    user: replyUserData || {
                      fullName: 'Unknown User',
                      userName: '',
                      profilePictureUrl: '',
                      userSubType: '',
                      idVerificationStatus: ''
                    }
                  };
                })
              );
              
              // Check if there are more replies
              const hasMoreReplies = repliesSnapshot.docs.length === 2;
              const repliesCount = data.repliesCount || 0;
              
              return {
                id: feedbackDoc.id,
                ...data,
                tags: feedbackTags, // Store tags as object for UI
                tagsArray: data.tags || [], // Keep the original array
                user: userDataObj || {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                },
                replies: replies,
                hasMoreReplies: hasMoreReplies,
                showReplies: replies.length > 0,
                loadingMoreReplies: false,
                repliesCount: repliesCount,
                lastReplyDoc: repliesSnapshot.docs.length > 0 ? 
                  repliesSnapshot.docs[repliesSnapshot.docs.length - 1] : null
              };
            } catch (err) {
              console.error('Error fetching user data:', err);
              
              return {
                id: feedbackDoc.id,
                ...data,
                tags: feedbackTags,
                tagsArray: data.tags || [],
                user: {
                  fullName: 'Unknown User',
                  userName: '',
                  profilePictureUrl: '',
                  userSubType: '',
                  idVerificationStatus: ''
                },
                replies: [],
                hasMoreReplies: false,
                showReplies: false,
                loadingMoreReplies: false,
                repliesCount: 0,
                lastReplyDoc: null
              };
            }
          })
        );
        
        if (isInitial) {
          setDisplayedFeedback(feedbackData);
          
          // Update tag-specific cache
          setTagFilteredFeedback(prev => ({
            ...prev,
            [tagFilter]: feedbackData
          }));
          
          // If this is the initial load, also update the full feedback list
          if (tagFilter === 'all') {
            setFeedbackList(feedbackData);
          }
        } else {
          const newDisplayedFeedback = [...displayedFeedback, ...feedbackData];
          setDisplayedFeedback(newDisplayedFeedback);
          
          // Update tag-specific cache for "load more"
          setTagFilteredFeedback(prev => ({
            ...prev,
            [tagFilter]: newDisplayedFeedback
          }));
          
          // If loading more for 'all' tag, update the full feedback list too
          if (tagFilter === 'all') {
            setFeedbackList(prev => [...prev, ...feedbackData]);
          }
        }
      }
      
      // Set active tag filter if it changed
      if (tagFilter !== activeTagFilter) {
        setActiveTagFilter(tagFilter);
      }
      
      // Extract available tags if initial load
      if (isInitial && (tagFilter === 'all' || !availableTags.length)) {
        fetchAvailableTags();
      }
    } catch (err) {
      console.error('Error loading feedback:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load feedback. Please try again.',
        type: 'error'
      });
    } finally {
      setLoadingFeedback(false);
    }
  }, [profileData?.userId, tagFilteredFeedback, tagLastFeedbackDocs, activeTagFilter, availableTags.length, displayedFeedback, showCustomAlert]);
  
  // Extract available tags from all feedback
  const fetchAvailableTags = useCallback(async () => {
    if (!profileData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      const feedbackRef = collection(db, 'users', profileData.userId, 'feedback');
      const feedbackSnapshot = await getDocs(feedbackRef);
      
      // Collect all tags used in feedback
      const tagsUsage = {};
      
      feedbackSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.tags && Array.isArray(data.tags)) {
          data.tags.forEach(tag => {
            tagsUsage[tag] = (tagsUsage[tag] || 0) + 1;
          });
        }
      });
      
      // Sort tags by usage count (descending)
      const sortedTags = Object.entries(tagsUsage)
        .sort((a, b) => b[1] - a[1])
        .map(([tag]) => tag);
      
      setAvailableTags(sortedTags);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  }, [profileData?.userId]);
  
 // Add this new function to fetch a single feedback item by ID
const fetchSingleFeedbackById = useCallback(async (feedbackId) => {
  if (!profileData?.userId || !feedbackId) return;
  
  try {
    setIsLoading(true);
    
    const db = getFirestoreInstance();
    const feedbackRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', feedbackId);
    const feedbackDoc = await getDoc(feedbackRef);
    
    if (!feedbackDoc.exists()) {
      showCustomAlert({
        title: 'Error',
        message: 'The feedback you\'re looking for could not be found.',
        type: 'error'
      });
      setIsLoading(false);
      return;
    }
    
    const data = feedbackDoc.data();
    
    // Convert tags array to object for consistent UI handling
    const feedbackTags = convertTagsToObject(data.tags);
    
    // Try to get user data
    let userDataObj = null;
    
    try {
      // Fetch user data
      const userDocRef = firestoreDoc(db, 'users', data.userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userDataObj = {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        };
      }
      
      // Fetch replies if any
      const repliesRef = collection(db, 'users', profileData.userId, 'feedback', feedbackId, 'replies');
      const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'));
      const repliesSnapshot = await getDocs(repliesQuery);
      
      const replies = await Promise.all(
        repliesSnapshot.docs.map(async (replyDoc) => {
          const replyData = replyDoc.data();
          let replyUserData = null;
          
          // Fetch reply user data
          try {
            const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
            const replyUserDoc = await getDoc(replyUserDocRef);
            
            if (replyUserDoc.exists()) {
              const userData = replyUserDoc.data();
              replyUserData = {
                fullName: userData.fullName || '',
                userName: userData.userName || '',
                profilePictureUrl: userData.profilePictureUrl || '',
                userSubType: userData.userSubType || '',
                idVerificationStatus: userData.idVerificationStatus || ''
              };
            }
          } catch (err) {
            console.error('Error fetching reply user data:', err);
          }
          
          return {
            id: replyDoc.id,
            ...replyData,
            user: replyUserData || {
              fullName: 'Unknown User',
              userName: '',
              profilePictureUrl: '',
              userSubType: '',
              idVerificationStatus: ''
            }
          };
        })
      );
      
      const singleFeedbackItem = {
        id: feedbackDoc.id,
        ...data,
        tags: feedbackTags, // Store tags as object for UI
        tagsArray: data.tags || [], // Keep the original array
        user: userDataObj || {
          fullName: 'Unknown User',
          userName: '',
          profilePictureUrl: '',
          userSubType: '',
          idVerificationStatus: ''
        },
        replies: replies,
        hasMoreReplies: false,
        showReplies: replies.length > 0,
        loadingMoreReplies: false,
        repliesCount: replies.length,
        lastReplyDoc: null
      };
      
      setSingleFeedback(singleFeedbackItem);
      setIsSingleItemView(true);
      setSingleItemId(feedbackId);
      
      // Also check if current user has already submitted feedback and it's this one
      if (userData?.userId === data.userId) {
        setUserFeedback({
          comment: data.comment || '',
          tags: feedbackTags
        });
        
        setEditingFeedback({
          id: feedbackDoc.id,
          ...data,
          tags: feedbackTags,
          tagsArray: data.tags || []
        });
      }
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load feedback details. Please try again.',
        type: 'error'
      });
    }
  } catch (err) {
    console.error('Error fetching single feedback:', err);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to load feedback. Please try again.',
      type: 'error'
    });
  } finally {
    setIsLoading(false);
  }
}, [profileData?.userId, userData?.userId, showCustomAlert]);

// Handle back button click
const handleBackToAllFeedback = () => {
  setIsSingleItemView(false);
  setSingleFeedback(null);
  setSingleItemId(null);
  
  // Load the regular feedback list
  loadFeedback(true, 'all', true);
};

// Modify your initial data loading effect to check for an item ID in the URL
useEffect(() => {
  if (profileData?.userId) {
    // Reset state when profile changes
    setDisplayedFeedback([]);
    setLastFeedbackDoc(null);
    setHasMoreFeedback(true);
    setActiveTagFilter('all');
    
    // Clear tag cache when profile changes
    clearTagCache();
    
    // Check URL parameters for specific feedback ID
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');
    const feedbackId = urlParams.get('id');
    
    if (section === 'feedback' && feedbackId) {
      // Fetch single feedback by ID
      fetchSingleFeedbackById(feedbackId);
    } else {
      // Load initial feedback normally
      setIsLoading(true);
      loadFeedback(true, 'all', true).finally(() => {
        setIsLoading(false);
      });
    }
    
    // Check if current user has already submitted feedback
    const checkExistingFeedback = async () => {
      if (!userData?.userId) return;
      
      try {
        const db = getFirestoreInstance();
        const feedbackRef = collection(db, 'users', profileData.userId, 'feedback');
        const userFeedbackQuery = query(feedbackRef, where('userId', '==', userData.userId));
        const userFeedbackSnapshot = await getDocs(userFeedbackQuery);
        
        if (!userFeedbackSnapshot.empty) {
          const feedbackData = userFeedbackSnapshot.docs[0].data();
          
          // Convert tags array to object for the form
          const tagsObject = convertTagsToObject(feedbackData.tags || []);
          
          setUserFeedback({
            comment: feedbackData.comment || '',
            tags: tagsObject
          });
          
          setEditingFeedback({
            id: userFeedbackSnapshot.docs[0].id,
            ...feedbackData,
            tags: tagsObject,
            tagsArray: feedbackData.tags || []
          });
        } else {
          // Reset user feedback form
          setUserFeedback({
            comment: '',
            tags: {}
          });
          setEditingFeedback(null);
        }
      } catch (err) {
        console.error('Error checking existing feedback:', err);
      }
    };
    
    checkExistingFeedback();
    fetchFeedbackCount();
  }
}, [profileData?.userId, userData?.userId, fetchSingleFeedbackById]);
  
  // Handle tag selection
  const handleTagSelect = (tag) => {
    setUserFeedback(prev => {
      const tags = { ...prev.tags };
      tags[tag] = !tags[tag];
      
      return { ...prev, tags };
    });
  };
  
  // Handle comment input
  const handleCommentChange = (e) => {
    const comment = e.target.value;
    if (comment.length <= 1000) {
      setUserFeedback(prev => ({ ...prev, comment }));
    }
  };
  
  // Handle tag filter change with loading indicator
  const handleTagFilterChange = (tag) => {
    if (tag === activeTagFilter) return;
    
    // Show feedback are loading
    setLoadingFeedback(true);
    
    // Reset current displayed feedback while loading
    setDisplayedFeedback([]);
    
    // Set the active tag immediately for better UX
    setActiveTagFilter(tag);
    
    // Load feedback with new filter (after a brief delay for UI update)
    setTimeout(() => {
      loadFeedback(true, tag);
    }, 100);
  };
  
  // Submit feedback
// Submit feedback
const handleSubmitFeedback = async () => {
  if (!userData?.userId || !profileData?.userId || !userFeedback.comment.trim()) {
    showCustomAlert({
      title: 'Error',
      message: 'Please sign in and provide feedback before submitting',
      type: 'error'
    });
    return;
  }
  
  setIsSubmitting(true);
  try {
    const db = getFirestoreInstance();
    
    // Get the user document to check current feedback count
    const userDocRef = firestoreDoc(db, 'users', profileData.userId);
    const userDoc = await getDoc(userDocRef);
    const userDocData = userDoc.exists() ? userDoc.data() : {};
    
    // Convert tags object to array
    const tagsArray = convertTagsToArray(userFeedback.tags);
    
    // Create feedback data
    const feedbackData = {
      userId: userData.userId,
      comment: userFeedback.comment,
      tags: tagsArray, // Store tags as an array
      timestamp: new Date().toISOString(),
      edited: editingFeedback ? true : false,
      repliesCount: editingFeedback ? (editingFeedback.repliesCount || 0) : 0
    };
    
    let feedbackId = editingFeedback ? editingFeedback.id : null;
    
    if (editingFeedback) {
      // Update existing feedback
      const feedbackRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', editingFeedback.id);
      await updateDoc(feedbackRef, feedbackData);
      
      // Send notification for feedback update
      await createNotification(profileData.userId, 'general', {
        type: 'FEEDBACK_UPDATED',
        title: 'Feedback Updated',
        message: `${userData.fullName || 'Someone'} has updated their feedback on your profile.`,
        data: {
          userId: userData.userId,
          profileId: profileData.userId,
          feedbackId: editingFeedback.id,
          _displayData: {
            fullName: userData.fullName,
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
      
      // Update the feedback in the list
      const updateFeedback = prev => 
        prev.map(feedback => 
          feedback.id === editingFeedback.id 
            ? {
                ...feedback,
                comment: userFeedback.comment,
                tags: userFeedback.tags, // Keep as object for UI display
                tagsArray: tagsArray, // Add the array version
                edited: true,
                timestamp: new Date().toISOString()
              }
            : feedback
        );
      
      setDisplayedFeedback(updateFeedback);
      setFeedbackList(updateFeedback);
      
      // Clear tag cache since data has changed
      clearTagCache();
      
      showCustomAlert({
        title: 'Success',
        message: 'Your feedback has been updated!',
        type: 'success'
      });
    } else {
      // Add new feedback
      const feedbackRef = collection(db, 'users', profileData.userId, 'feedback');
      const newFeedbackRef = await addDoc(feedbackRef, feedbackData);
      
      // Store the new feedback ID
      feedbackId = newFeedbackRef.id;
      
      // Update totalFeedback counter in user document
      const currentTotal = userDocData.totalFeedback || 0;
      await updateDoc(userDocRef, {
        totalFeedback: currentTotal + 1
      });
      
      // Update local profile data
      setProfileData(prev => ({
        ...prev,
        totalFeedback: (prev.totalFeedback || 0) + 1
      }));
      
      // Create notification for the user
      await createNotification(profileData.userId, 'general', {
        type: 'NEW_FEEDBACK',
        title: 'New Feedback',
        message: `${userData.fullName || 'Someone'} has left feedback on your profile.`,
        data: {
          userId: userData.userId,
          profileId: profileData.userId,
          feedbackId: feedbackId,
          _displayData: {
            fullName: userData.fullName,
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl || ''
          }
        }
      });
      
      // Add new feedback to the top of the list
      const newFeedback = {
        id: feedbackId,
        ...feedbackData,
        tags: userFeedback.tags, // Keep as object for UI display
        tagsArray: tagsArray, // Add the array version
        user: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        },
        replies: [],
        hasMoreReplies: false,
        showReplies: false,
        repliesCount: 0,
        lastReplyDoc: null
      };
      
      setDisplayedFeedback(prev => [newFeedback, ...prev]);
      setFeedbackList(prev => [newFeedback, ...prev]);
      
      // Clear tag cache since data has changed
      clearTagCache();
      
      // Update editing state for future edits
      setEditingFeedback(newFeedback);
      
      // Update available tags
      if (tagsArray.length > 0) {
        setAvailableTags(prev => {
          const newTags = new Set([...prev]);
          tagsArray.forEach(tag => newTags.add(tag));
          return Array.from(newTags);
        });
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Thank you for your feedback!',
        type: 'success'
      });
    }
    
    // Reset form
    setUserFeedback({
      comment: '',
      tags: {}
    });
    
    // Hide feedback form
    setShowFeedbackForm(false);
    
  } catch (err) {
    console.error('Error submitting feedback:', err);
    showCustomAlert({
      title: 'Error',
      message: 'Failed to submit feedback. Please try again.',
      type: 'error'
    });
  } finally {
    setIsSubmitting(false);
  }
};
  
  // Delete feedback
  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }
    
    try {
      const db = getFirestoreInstance();
      const feedbackRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', feedbackId);
      
      // Get the user document reference
      const userDocRef = firestoreDoc(db, 'users', profileData.userId);
      
      // Run transaction to delete feedback and all replies
      await runTransaction(db, async (transaction) => {
        // PERFORM ALL READS FIRST
        
        // Read user document
        const userDoc = await transaction.get(userDocRef);
        const userDocData = userDoc.exists() ? userDoc.data() : {};
        
        // Read all replies to delete
        const repliesRef = collection(db, 'users', profileData.userId, 'feedback', feedbackId, 'replies');
        const repliesSnapshot = await getDocs(repliesRef);
        
        // NOW PERFORM ALL WRITES
        
        // Update totalFeedback counter in user document
        const currentTotal = userDocData.totalFeedback || 0;
        if (currentTotal > 0) {
          transaction.update(userDocRef, {
            totalFeedback: currentTotal - 1
          });
        }
        
        // Delete all replies
        repliesSnapshot.docs.forEach(doc => {
          transaction.delete(firestoreDoc(repliesRef, doc.id));
        });
        
        // Delete the feedback document
        transaction.delete(feedbackRef);
      });
      
      // Update local profile data
      setProfileData(prev => ({
        ...prev,
        totalFeedback: Math.max(0, (prev.totalFeedback || 0) - 1)
      }));
      
      // Update local state
      setDisplayedFeedback(prev => prev.filter(feedback => feedback.id !== feedbackId));
      setFeedbackList(prev => prev.filter(feedback => feedback.id !== feedbackId));
      
      // Clear tag cache since data has changed
      clearTagCache();
      
      // Reset editing state if deleting the feedback being edited
      if (editingFeedback && editingFeedback.id === feedbackId) {
        setEditingFeedback(null);
        setUserFeedback({
          comment: '',
          tags: {}
        });
      }
      
      // Re-fetch available tags in case this was the only feedback with certain tags
      fetchAvailableTags();
      
      showCustomAlert({
        title: 'Success',
        message: 'Feedback deleted successfully',
        type: 'success'
      });
      
    } catch (err) {
      console.error('Error deleting feedback:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to delete feedback. Please try again.',
        type: 'error'
      });
    }
  };
  
  // Load more replies for a feedback
  const handleLoadMoreReplies = async (feedbackId, lastReplyDoc) => {
    if (!feedbackId || !lastReplyDoc) return;
    
    // Update loading state for this feedback
    setDisplayedFeedback(prev => 
      prev.map(feedback => 
        feedback.id === feedbackId 
          ? { ...feedback, loadingMoreReplies: true }
          : feedback
      )
    );
    
    // Also update singleFeedback loading state if in single item view
    if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
      setSingleFeedback(prev => ({
        ...prev,
        loadingMoreReplies: true
      }));
    }
    
    try {
      const db = getFirestoreInstance();
      const repliesRef = collection(db, 'users', profileData.userId, 'feedback', feedbackId, 'replies');
      const repliesQuery = query(repliesRef, orderBy('timestamp', 'asc'), startAfter(lastReplyDoc), limit(2));
      const repliesSnapshot = await getDocs(repliesQuery);
      
      if (!repliesSnapshot.empty) {
        const newReplies = await Promise.all(
          repliesSnapshot.docs.map(async (replyDoc) => {
            const replyData = replyDoc.data();
            let replyUserData = null;
            
            // Fetch reply user data
            try {
              const replyUserDocRef = firestoreDoc(db, 'users', replyData.userId);
              const replyUserDoc = await getDoc(replyUserDocRef);
              
              if (replyUserDoc.exists()) {
                const userData = replyUserDoc.data();
                replyUserData = {
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (err) {
              console.error('Error fetching reply user data:', err);
            }
            
            return {
              id: replyDoc.id,
              ...replyData,
              user: replyUserData || {
                fullName: 'Unknown User',
                userName: '',
                profilePictureUrl: '',
                userSubType: '',
                idVerificationStatus: ''
              }
            };
          })
        );
        
        // Update the feedback with new replies and lastReplyDoc
        const updateFeedback = prev => 
          prev.map(feedback => 
            feedback.id === feedbackId 
              ? {
                  ...feedback,
                  replies: [...feedback.replies, ...newReplies],
                  lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
                  hasMoreReplies: repliesSnapshot.docs.length === 2,
                  loadingMoreReplies: false
                }
              : feedback
          );
        
        setDisplayedFeedback(updateFeedback);
        setFeedbackList(updateFeedback);
        
        // Also update the cached feedback data
        setTagFilteredFeedback(prev => {
          const newCache = { ...prev };
          
          Object.keys(newCache).forEach(tag => {
            if (newCache[tag]) {
              newCache[tag] = newCache[tag].map(feedback => 
                feedback.id === feedbackId 
                  ? {
                      ...feedback,
                      replies: [...feedback.replies, ...newReplies],
                      lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
                      hasMoreReplies: repliesSnapshot.docs.length === 2,
                      loadingMoreReplies: false
                    }
                  : feedback
              );
            }
          });
          
          return newCache;
        });
        
        // Also update singleFeedback if in single item view
        if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
          setSingleFeedback(prev => ({
            ...prev,
            replies: [...prev.replies, ...newReplies],
            lastReplyDoc: repliesSnapshot.docs[repliesSnapshot.docs.length - 1],
            hasMoreReplies: repliesSnapshot.docs.length === 2,
            loadingMoreReplies: false
          }));
        }
        
      } else {
        // No more replies
        const updateFeedback = prev => 
          prev.map(feedback => 
            feedback.id === feedbackId 
              ? { ...feedback, hasMoreReplies: false, loadingMoreReplies: false }
              : feedback
          );
        
        setDisplayedFeedback(updateFeedback);
        setFeedbackList(updateFeedback);
        
        // Also update the cached feedback data
        setTagFilteredFeedback(prev => {
          const newCache = { ...prev };
          
          Object.keys(newCache).forEach(tag => {
            if (newCache[tag]) {
              newCache[tag] = newCache[tag].map(feedback => 
                feedback.id === feedbackId 
                  ? { ...feedback, hasMoreReplies: false, loadingMoreReplies: false }
                  : feedback
              );
            }
          });
          
          return newCache;
        });
        
        // Also update singleFeedback if in single item view
        if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
          setSingleFeedback(prev => ({
            ...prev,
            hasMoreReplies: false,
            loadingMoreReplies: false
          }));
        }
      }
    } catch (err) {
      console.error('Error loading more replies:', err);
      
      // Reset loading state
      setDisplayedFeedback(prev => 
        prev.map(feedback => 
          feedback.id === feedbackId 
            ? { ...feedback, loadingMoreReplies: false }
            : feedback
        )
      );
      
      // Also reset singleFeedback loading state if in single item view
      if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
        setSingleFeedback(prev => ({
          ...prev,
          loadingMoreReplies: false
        }));
      }
    }
  };
  
  
  // Toggle showing/hiding replies
  const handleToggleReplies = (feedbackId) => {
    // Update regular feedback lists
    const updateFeedback = prev => 
      prev.map(feedback => 
        feedback.id === feedbackId 
          ? { ...feedback, showReplies: !feedback.showReplies }
          : feedback
      );
    
    setDisplayedFeedback(updateFeedback);
    setFeedbackList(updateFeedback);
    
    // Also update the cached feedback data
    setTagFilteredFeedback(prev => {
      const newCache = { ...prev };
      
      Object.keys(newCache).forEach(tag => {
        if (newCache[tag]) {
          newCache[tag] = newCache[tag].map(feedback => 
            feedback.id === feedbackId 
              ? { ...feedback, showReplies: !feedback.showReplies }
              : feedback
          );
        }
      });
      
      return newCache;
    });
    
    // IMPORTANT: Also update the single feedback item if we're in single item view
    if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
      setSingleFeedback(prev => ({
        ...prev,
        showReplies: !prev.showReplies
      }));
    }
  };
  
  
  // Add reply to a feedback
  const handleAddReply = async (feedbackId, replyText) => {
    if (!userData?.userId || !replyText.trim()) return;
    
    try {
      const db = getFirestoreInstance();
      const repliesRef = collection(db, 'users', profileData.userId, 'feedback', feedbackId, 'replies');
      const feedbackRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', feedbackId);
      
      // Add reply
      const replyData = {
        userId: userData.userId,
        text: replyText.trim(),
        timestamp: new Date().toISOString()
      };
      
      const replyDocRef = await addDoc(repliesRef, replyData);
      
      // Update feedback's replies count
      const feedbackDoc = await getDoc(feedbackRef);
      if (feedbackDoc.exists()) {
        const feedbackData = feedbackDoc.data();
        await updateDoc(feedbackRef, {
          repliesCount: (feedbackData.repliesCount || 0) + 1
        });
      }
      
      // Add the new reply to the UI
      const newReply = {
        id: replyDocRef.id,
        ...replyData,
        user: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userSubType: userData.userSubType || '',
          idVerificationStatus: userData.idVerificationStatus || ''
        }
      };
      
      // Update the feedback with the new reply
      const updateFeedback = prev => 
        prev.map(feedback => 
          feedback.id === feedbackId 
            ? {
                ...feedback,
                replies: [...feedback.replies, newReply],
                repliesCount: (feedback.repliesCount || 0) + 1,
                showReplies: true
              }
            : feedback
        );
      
      setDisplayedFeedback(updateFeedback);
      setFeedbackList(updateFeedback);
      
      // Also update the cached feedback data
      setTagFilteredFeedback(prev => {
        const newCache = { ...prev };
        
        Object.keys(newCache).forEach(tag => {
          if (newCache[tag]) {
            newCache[tag] = newCache[tag].map(feedback => 
              feedback.id === feedbackId 
                ? {
                    ...feedback,
                    replies: [...feedback.replies, newReply],
                    repliesCount: (feedback.repliesCount || 0) + 1,
                    showReplies: true
                  }
                : feedback
            );
          }
        });
        
        return newCache;
      });
      
      // Also update singleFeedback if in single item view
      if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
        setSingleFeedback(prev => ({
          ...prev,
          replies: [...prev.replies, newReply],
          repliesCount: (prev.repliesCount || 0) + 1,
          showReplies: true
        }));
      }
      
      // Create notification for the feedback author
      const feedbackDoc2 = await getDoc(feedbackRef);
      if (feedbackDoc2.exists()) {
        const feedbackData = feedbackDoc2.data();
        
        if (feedbackData.userId !== userData.userId) {
          await createNotification(feedbackData.userId, 'general', {
            type: 'FEEDBACK_REPLY',
            title: 'Reply to Your Feedback',
            message: `${userData.fullName || 'Someone'} has replied to your feedback.`,
            data: {
              userId: userData.userId,
              profileId: profileData.userId,
              feedbackId: feedbackId,
              replyId: replyDocRef.id,
              _displayData: {
                fullName: userData.fullName,
                userName: userData.userName,
                profilePictureUrl: userData.profilePictureUrl || ''
              }
            }
          });
        }
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Reply added successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error adding reply:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to add reply. Please try again.',
        type: 'error'
      });
    }
  };
  
  // Delete reply
  const handleDeleteReply = async (feedbackId, replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }
    
    try {
      const db = getFirestoreInstance();
      const replyRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', feedbackId, 'replies', replyId);
      const feedbackRef = firestoreDoc(db, 'users', profileData.userId, 'feedback', feedbackId);
      
      // Delete reply
      await deleteDoc(replyRef);
      
      // Update feedback's replies count
      const feedbackDoc = await getDoc(feedbackRef);
      if (feedbackDoc.exists()) {
        const feedbackData = feedbackDoc.data();
        await updateDoc(feedbackRef, {
          repliesCount: Math.max(0, (feedbackData.repliesCount || 0) - 1)
        });
      }
      
      // Update UI
      const updateFeedback = prev => 
        prev.map(feedback => 
          feedback.id === feedbackId 
            ? {
                ...feedback,
                replies: feedback.replies.filter(reply => reply.id !== replyId),
                repliesCount: Math.max(0, (feedback.repliesCount || 0) - 1)
              }
            : feedback
        );
      
      setDisplayedFeedback(updateFeedback);
      setFeedbackList(updateFeedback);
      
      // Also update the cached feedback data
      setTagFilteredFeedback(prev => {
        const newCache = { ...prev };
        
        Object.keys(newCache).forEach(tag => {
          if (newCache[tag]) {
            newCache[tag] = newCache[tag].map(feedback => 
              feedback.id === feedbackId 
                ? {
                    ...feedback,
                    replies: feedback.replies.filter(reply => reply.id !== replyId),
                    repliesCount: Math.max(0, (feedback.repliesCount || 0) - 1)
                  }
                : feedback
            );
          }
        });
        
        return newCache;
      });
      
      // Also update singleFeedback if in single item view
      if (isSingleItemView && singleFeedback && singleFeedback.id === feedbackId) {
        setSingleFeedback(prev => ({
          ...prev,
          replies: prev.replies.filter(reply => reply.id !== replyId),
          repliesCount: Math.max(0, (prev.repliesCount || 0) - 1)
        }));
      }
      
      showCustomAlert({
        title: 'Success',
        message: 'Reply deleted successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error deleting reply:', err);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to delete reply. Please try again.',
        type: 'error'
      });
    }
  };
  
  
  // Handle editing a feedback
  const handleEditFeedback = (feedback) => {
    setEditingFeedback(feedback);
    setUserFeedback({
      comment: feedback.comment,
      tags: feedback.tags || {}
    });
    setShowFeedbackForm(true);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Render the feedback form modal
  const renderFeedbackForm = () => {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>{editingFeedback ? 'Edit Your Feedback' : 'Share Your Feedback'}</h2>
            <button 
              className={styles.closeButton}
              onClick={() => {
                setShowFeedbackForm(false);
                if (!editingFeedback) {
                  setUserFeedback({
                    comment: '',
                    tags: {}
                  });
                }
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.feedbackForm}>
            {/* Comment */}
            <div className={styles.commentSection}>
              <textarea
                placeholder="Share your feedback (max 1000 characters)"
                value={userFeedback.comment}
                onChange={handleCommentChange}
                className={styles.commentInput}
                maxLength={1000}
                required
              />
              <div className={styles.characterCount}>
                {userFeedback.comment.length}/1000
              </div>
            </div>
            
            {/* Tags */}
            <div className={styles.tagsSection}>
              <h3 className={styles.tagsTitle}>Select Tags (Optional)</h3>
              
              {/* Tag Categories */}
              <div className={styles.tagCategoriesContainer}>
                {Object.entries(tagCategories).map(([key, category]) => (
                  <div key={key} className={styles.tagCategory}>
                    <h4 className={styles.categoryTitle}>{category.title}</h4>
                    <div className={styles.tagsList}>
                      {category.tags.map(tag => (
                        <button
                          key={tag}
                          className={`${styles.tagButton} ${userFeedback.tags[tag] ? styles.tagSelected : ''}`}
                          onClick={() => handleTagSelect(tag)}
                        >
                          {tag}
                          {userFeedback.tags[tag] && (
                            <FontAwesomeIcon icon={faCheck} className={styles.tagCheckIcon} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Submit Button */}
            <div className={styles.formActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowFeedbackForm(false);
                  if (!editingFeedback) {
                    setUserFeedback({
                      comment: '',
                      tags: {}
                    });
                  }
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleSubmitFeedback}
                disabled={isSubmitting || !userFeedback.comment.trim()}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>{editingFeedback ? 'Update Feedback' : 'Submit Feedback'}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render tag filters
  const renderTagFilters = () => {
    // Combine 'all' with available tags (limit to top 8 tags)
    const displayableTags = ['all', ...availableTags.slice(0, 8)];
    
    return (
      <div className={styles.tagFilters}>
        <div className={styles.tagFiltersHeader}>
          <h3>Filter Feedback</h3>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
        </div>
        <div className={styles.tagFiltersList}>
          {displayableTags.map(tag => (
            <button
              key={tag}
              className={`${styles.tagFilterButton} ${activeTagFilter === tag ? styles.activeTagFilter : ''}`}
              onClick={() => handleTagFilterChange(tag)}
            >
              {tag === 'all' ? 'All Feedback' : tag}
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  // Render reply form
  const ReplyForm = ({ feedbackId, onSubmit }) => {
    const [replyText, setReplyText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleSubmit = async () => {
      if (!replyText.trim()) return;
      
      setIsSubmitting(true);
      try {
        await onSubmit(feedbackId, replyText);
        setReplyText('');
      } finally {
        setIsSubmitting(false);
      }
    };
    
    return (
      <div className={styles.replyForm}>
        <textarea
          placeholder="Write a reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          className={styles.replyInput}
          maxLength={500}
        />
        <div className={styles.replyFormActions}>
          <div className={styles.characterCount}>
            {replyText.length}/500
          </div>
          <button
            className={styles.replyButton}
            onClick={handleSubmit}
            disabled={isSubmitting || !replyText.trim()}
          >
            {isSubmitting ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <span>Reply</span>
            )}
          </button>
        </div>
      </div>
    );
  };
  
  // Render feedback item
  const FeedbackItem = ({ feedback }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    
    return (
      <div 
      key={feedback.id} 
      className={styles.feedbackItem}
      id={`feedback-${feedback.id}`}  // Add this ID
    >
        <div className={styles.feedbackHeader}>
          <img
            src={feedback.user.profilePictureUrl || defaultUserImage}
            alt={feedback.user.fullName}
            className={styles.userAvatar}
          />
         <div 
  className={styles.userInfo}
  onClick={() => navigateToUserProfile(feedback.user.userName)}
  style={{ cursor: 'pointer' }}
>
  <div className={styles.userName}>
    {feedback.user.fullName}
    {feedback.user.idVerificationStatus === 'Verified' && (
      <img 
        src={verifiedBadge} 
        alt="Verified" 
        className={styles.verifiedBadge} 
      />
    )}
  </div>
  <div className={styles.userDetails}>
    @{feedback.user.userName} • {feedback.user.userSubType}
  </div>
</div>
          <div className={styles.feedbackDate}>
            {formatDate(feedback.timestamp)}
            {feedback.edited && <span className={styles.editedBadge}>(Edited)</span>}
          </div>
        </div>
        
        <div className={styles.feedbackComment}>
          {feedback.comment}
        </div>
        
        {feedback.tags && Object.keys(feedback.tags).length > 0 && (
          <div className={styles.feedbackTags}>
            {Object.entries(feedback.tags)
              .filter(([_, value]) => value === true)
              .map(([tag]) => (
                <span key={tag} className={styles.feedbackTag}>
                  {tag}
                </span>
              ))}
          </div>
        )}
        
        <div className={styles.feedbackActions}>
          {isOwnProfile && (
            <button
              className={styles.replyButton}
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              <FontAwesomeIcon icon={faReply} />
              <span>{showReplyForm ? 'Cancel Reply' : 'Reply'}</span>
            </button>
          )}
          
          {userData?.userId === feedback.userId && (
            <>
              <button
                className={styles.editButton}
                onClick={() => handleEditFeedback(feedback)}
              >
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit</span>
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => handleDeleteFeedback(feedback.id)}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
        
        {showReplyForm && (
          <ReplyForm 
            feedbackId={feedback.id} 
            onSubmit={handleAddReply}
          />
        )}
        
        {/* Replies Section */}
        {feedback.repliesCount > 0 && (
          <div className={styles.repliesSection}>
            <button
              className={styles.toggleRepliesButton}
              onClick={() => handleToggleReplies(feedback.id)}
            >
              <FontAwesomeIcon 
                icon={feedback.showReplies ? faChevronUp : faChevronDown} 
                className={styles.toggleIcon}
              />
              <span>
                {feedback.showReplies 
                  ? 'Hide Replies' 
                  : `Show Replies (${feedback.repliesCount})`}
              </span>
            </button>
            
            {feedback.showReplies && (
              <div className={styles.repliesList}>
                {feedback.replies.map(reply => (
                  <div key={reply.id} className={styles.replyItem}>
                    <div className={styles.replyHeader}>
                      <img
                        src={reply.user.profilePictureUrl || defaultUserImage}
                        alt={reply.user.fullName}
                        className={styles.replyUserAvatar}
                      />
                     <div 
  className={styles.replyUserInfo}
  onClick={() => navigateToUserProfile(reply.user.userName)}
  style={{ cursor: 'pointer' }}
>
                        <div className={styles.replyUserName}>
                          {reply.user.fullName}
                          {reply.user.idVerificationStatus === 'Verified' && (
                            <img 
                              src={verifiedBadge} 
                              alt="Verified" 
                              className={styles.verifiedBadgeSmall} 
                            />
                          )}
                          <span className={styles.replyUserRole}>
                            • {reply.user.userSubType}
                          </span>
                        </div>
                        <div className={styles.replyTimestamp}>
                          {formatDate(reply.timestamp)}
                        </div>
                      </div>
                      
                      {(isOwnProfile || userData?.userId === reply.userId) && (
                        <button
                          className={styles.deleteReplyButton}
                          onClick={() => handleDeleteReply(feedback.id, reply.id)}
                          title="Delete Reply"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    <div className={styles.replyText}>
                      {reply.text}
                    </div>
                  </div>
                ))}
                
                {feedback.hasMoreReplies && (
                  <button
                    className={styles.loadMoreRepliesButton}
                    onClick={() => handleLoadMoreReplies(feedback.id, feedback.lastReplyDoc)}
                    disabled={feedback.loadingMoreReplies}
                  >
                    {feedback.loadingMoreReplies ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Load More Replies</span>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
  <div className={styles.feedbackSection}>
    <h2 className={styles.sectionTitle}>
      Feedback {profileData.totalFeedback ? `(${profileData.totalFeedback})` : ''}
    </h2>
    
    {/* Single Feedback View */}
    {isSingleItemView && singleFeedback ? (
      <div className={styles.singleItemView}>
        <div className={styles.singleItemHeader}>
          <button 
            className={styles.backButton}
            onClick={handleBackToAllFeedback}
          >
            ← Back to all feedback
          </button>
        </div>
        
        <FeedbackItem feedback={singleFeedback} />
      </div>
    ) : (
      /* Regular feedback list view - keep your existing code here */
      <>
        {/* Tag Filters */}
        {availableTags.length > 0 && renderTagFilters()}
        
        {/* Add Feedback Button */}
        {userData && !isOwnProfile && (
          <button 
            className={styles.addFeedbackButton}
            onClick={() => setShowFeedbackForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{editingFeedback ? 'Edit Your Feedback' : 'Add Feedback'}</span>
          </button>
        )}
        
        {/* Feedback List with improved loading state */}
        {isLoading || (loadingFeedback && displayedFeedback.length === 0) ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading feedback...</p>
          </div>
        ) : displayedFeedback.length > 0 ? (
          <div className={styles.feedbackList}>
            {displayedFeedback.map(feedback => (
              <FeedbackItem key={feedback.id} feedback={feedback} />
            ))}
            
            {hasMoreFeedback && (
              <button
                className={styles.loadMoreButton}
                onClick={() => loadFeedback(false, activeTagFilter)}
                disabled={loadingFeedback}
              >
                {loadingFeedback ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More Feedback</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <FontAwesomeIcon icon={faComment} />
            </div>
            <p>No feedback yet. Be the first to leave feedback!</p>
          </div>
        )}
      </>
    )}
    
    {/* Feedback Form Modal */}
    {showFeedbackForm && renderFeedbackForm()}
  </div>
);

};

export default FeedbackSection;